import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FilePenLine } from 'lucide-react'

import { Trash01 } from '@/components/Icons'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { ReminderStatus } from '@/schema'

import type { ReminderConfiguration } from '../types'
import { getThresholdDescription } from '../utils'

interface ReminderItemProps {
  reminder: ReminderConfiguration
  onEdit?: (name: string, status: string, unit: string, value: number) => void
  onDelete?: (reminder: ReminderConfiguration) => void
  onEditClick?: (reminder: ReminderConfiguration) => void
  isLoading?: boolean
}

const ReminderItemComponent: React.FC<ReminderItemProps> = ({
  reminder,
  onEdit,
  onDelete,
  onEditClick,
  isLoading = false,
}) => {
  const { t } = useTranslation()

  const reminderDescription = useMemo(
    () => getThresholdDescription(reminder, t),
    [reminder, t]
  )

  const reminderName = useMemo(() => {
    if (reminder.is_default && reminder.name.startsWith('workflowConfig.')) {
      return t(reminder.name)
    }
    return reminder.name
  }, [reminder.is_default, reminder.name, t])

  const isDefault = useMemo(
    () => reminder.is_default === true,
    [reminder.is_default]
  )

  return (
    <div className="flex items-center gap-4 px-6 py-4 bg-white border border-solid rounded-lg border-gray-light-300">
      <div className="flex items-center flex-shrink-0">
        <Switch
          checked={reminder.status === ReminderStatus.Active}
          onCheckedChange={(checked) => {
            const newStatus = checked
              ? ReminderStatus.Active
              : ReminderStatus.Inactive
            if (onEdit) {
              onEdit(reminder.name, newStatus, reminder.unit, reminder.value)
            }
          }}
          disabled={isLoading}
          size="md"
        />
      </div>

      <div className="flex flex-col justify-center flex-1 min-w-0">
        <h3 className="m-0 text-base font-medium text-gray-light-900">
          {reminderName}
        </h3>
        <p className="m-0 mt-1 text-sm text-gray-light-500">
          {reminderDescription}
        </p>
      </div>

      {!isDefault && (
        <div className="flex items-center flex-shrink-0 gap-2">
          <Button
            variant="text"
            onClick={() => onEditClick?.(reminder)}
            disabled={isLoading}
            className="p-2 text-gray-light-500 hover:text-gray-light-900"
          >
            <FilePenLine className="w-5 h-5" />
          </Button>
          <Button
            variant="text"
            onClick={() => onDelete?.(reminder)}
            disabled={isLoading}
            className="p-2 text-gray-light-500 hover:text-red-600"
          >
            <Trash01 className="w-5 h-5" />
          </Button>
        </div>
      )}
    </div>
  )
}

export const ReminderItem = React.memo(ReminderItemComponent)
